/**
 * Canvas utility functions for QR code generation and manipulation
 */

/**
 * Draws a rounded rectangle on the canvas context
 * @param {CanvasRenderingContext2D} ctx - The canvas 2D context
 * @param {number} x - The x coordinate
 * @param {number} y - The y coordinate
 * @param {number} width - The width of the rectangle
 * @param {number} height - The height of the rectangle
 * @param {number} radius - The corner radius
 */
export const drawRoundedRect = (ctx, x, y, width, height, radius) => {
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
};

/**
 * Calculates dimensions for the icon overlay
 * @param {number} qrSize - The size of the QR code
 * @param {number} [iconSizeRatio=0.15] - The ratio of icon size to QR code size
 * @returns {Object} The calculated dimensions
 */
export const calculateIconDimensions = (qrSize, iconSizeRatio = 0.15) => {
  const iconSize = qrSize * iconSizeRatio;
  return {
    size: iconSize,
    x: (qrSize - iconSize) / 2,
    y: (qrSize - iconSize) / 2,
    borderRadius: iconSize * 0.2,
  };
};

/**
 * Creates a clip path for the icon
 * @param {CanvasRenderingContext2D} ctx - The canvas 2D context
 * @param {Object} dimensions - The icon dimensions
 */
export const createIconClipPath = (ctx, { x, y, size, borderRadius }) => {
  ctx.beginPath();
  drawRoundedRect(ctx, x, y, size, size, borderRadius);
  ctx.clip();
};

/**
 * Draws an icon on the canvas with a rounded border
 * @param {HTMLCanvasElement} canvas - The canvas element
 * @param {HTMLImageElement} icon - The icon image element
 * @param {Object} dimensions - The icon dimensions
 */
export const drawIconOnCanvas = (canvas, icon, dimensions) => {
  const ctx = canvas.getContext('2d');
  ctx.save();
  createIconClipPath(ctx, dimensions);
  ctx.drawImage(icon, dimensions.x, dimensions.y, dimensions.size, dimensions.size);
  ctx.restore();
};
